/**
 * Paywall copy + context helpers shared by MobilePaywall and the walls that
 * open it.
 *
 * The native paywall opens from exactly three kinds of places:
 *   1. a free wall inside a feature (For You, Mixpedia "See all N", Crate
 *      Digger, Set Planner, Notifications) — `feature` is the gate key and
 *      `context` carries what the user was looking at (`moreCount` / `total`)
 *      so the headline names it;
 *   2. the one auto-presented moment, the trial-end sheet
 *      (`TRIAL_ENDED_FEATURE`, see utils/trialEndSheet.js);
 *   3. the Subscription tab / trial banner — no feature, generic headline.
 */

export const TRIAL_ENDED_FEATURE = 'trial_ended';

// `requireUpgrade` is wired straight into onClick in many places, so the first
// argument is often a React synthetic event. Only a plain data object counts
// as paywall context; anything event-shaped is dropped.
export function asPaywallContext(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    if ('nativeEvent' in value || 'preventDefault' in value || 'target' in value) return null;
    return value;
}

const count = (n) => (Number.isFinite(n) && n > 0 ? n : 0);
const tracks = (n) => `${n.toLocaleString()} more track${n === 1 ? '' : 's'}`;

/**
 * Headline for the paywall sheet.
 * @param {{feature?: string|null, context?: object|null, trialLabel?: string|null}} args
 *   `trialLabel` = a StoreKit / Play free-trial offer label ("14 days") when
 *   one applies; only the generic headline uses it.
 */
export function paywallHeadline({ feature, context, trialLabel } = {}) {
    const more = count(Number(context?.moreCount));
    const total = count(Number(context?.total));
    switch (feature) {
        case TRIAL_ENDED_FEATURE:
            return 'Your GOAT trial has ended';
        case 'feed': {
            if (!more) return 'Unlock your full For You feed';
            if (context?.list === 'mixes') return `Unlock ${more.toLocaleString()} more set${more === 1 ? '' : 's'} for you`;
            if (context?.list === 'trending') return `Unlock ${tracks(more)} heating up right now`;
            return `Unlock ${tracks(more)} for you`;
        }
        case 'full-mixpedia':
            return total ? `See all ${total.toLocaleString()} results` : 'See every result in Mixpedia';
        case 'smart-recommendations':
            return more ? `Unlock ${tracks(more)} for this playlist` : 'Unlock the full Crate Digger';
        case 'whats-next':
            return more ? `Unlock ${tracks(more)} for this set` : 'Plan sets without limits';
        case 'notifications':
            return 'Never miss a set from the artists you follow';
        default:
            return trialLabel ? `Start your ${trialLabel} free trial` : 'Unlock MixGoat GOAT';
    }
}

/** Optional line under the headline. Only the trial-end sheet has one. */
export function paywallSubline(feature) {
    if (feature === TRIAL_ENDED_FEATURE) {
        return "You're on the Free plan now, and everything you made is still yours. GOAT keeps the rest open:";
    }
    return null;
}
